import React, { useContext, useEffect, useMemo, useState } from "react";
import { FiChevronDown, FiFilter, FiSearch, FiX } from "react-icons/fi";
import { useSearchParams } from "react-router-dom";
import productData, {
  productCategories,
  productSubcategories,
} from "../Datas/productData";
import { CartContext } from "./CartContext";
import PageMeta from "./PageMeta";
import ProductCard from "./ProductCard";
import "../css/shop.scss";

const origins = ["Hong Kong selection"];

const originMatches = (origin, filter) => origin.includes(filter);

const FilterGroup = ({ title, open = true, children }) => (
  <details className="filter-group" open={open}>
    <summary>
      {title}
      <FiChevronDown />
    </summary>
    <div className="filter-group__body">{children}</div>
  </details>
);

function Shop() {
  const { wishlist } = useContext(CartContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedSubcategories, setSelectedSubcategories] = useState([]);
  const [selectedOrigins, setSelectedOrigins] = useState([]);
  const [maxPrice, setMaxPrice] = useState(180);
  const [sort, setSort] = useState("featured");
  const [filterOpen, setFilterOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const category = searchParams.get("category") || "All";
  const query = searchParams.get("q") || "";
  const collection = searchParams.get("collection") || "";
  const favouritesOnly = searchParams.get("favourites") === "true";

  useEffect(() => {
    setLoading(true);
    const timer = window.setTimeout(() => setLoading(false), 180);
    return () => window.clearTimeout(timer);
  }, [
    category,
    collection,
    favouritesOnly,
    maxPrice,
    query,
    selectedOrigins,
    selectedSubcategories,
    sort,
  ]);

  useEffect(() => {
    document.body.classList.toggle("filter-open", filterOpen);
    return () => document.body.classList.remove("filter-open");
  }, [filterOpen]);

  const updateParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === "All") next.delete(key);
    else next.set(key, value);
    setSearchParams(next);
  };

  const toggleArrayValue = (setter, value) => {
    setter((items) =>
      items.includes(value)
        ? items.filter((item) => item !== value)
        : [...items, value]
    );
  };

  const filteredProducts = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    const products = productData.filter((product) => {
      const inCategory = category === "All" || product.category === category;
      const inSubcategory =
        selectedSubcategories.length === 0 ||
        selectedSubcategories.includes(product.subcategory);
      const inOrigin =
        selectedOrigins.length === 0 ||
        selectedOrigins.some((origin) =>
          originMatches(product.attributes.origin, origin)
        );
      const inPrice = product.price <= maxPrice;
      const inSearch =
        !normalizedQuery ||
        `${product.name} ${product.category} ${product.subcategory} ${
          product.attributes.origin
        } ${product.tags.join(" ")}`
          .toLowerCase()
          .includes(normalizedQuery);
      const inCollection =
        !collection ||
        (collection === "deals" && Boolean(product.compareAtPrice)) ||
        (collection === "bestsellers" && product.bestseller) ||
        (collection === "monthly" &&
          (product.newArrival || product.bestseller));
      const inFavourites = !favouritesOnly || wishlist.includes(product.id);
      return (
        inCategory &&
        inSubcategory &&
        inOrigin &&
        inPrice &&
        inSearch &&
        inCollection &&
        inFavourites
      );
    });

    return [...products].sort((a, b) => {
      if (sort === "price-low") return a.price - b.price;
      if (sort === "price-high") return b.price - a.price;
      if (sort === "name") return a.name.localeCompare(b.name);
      if (sort === "newest") return Number(b.newArrival) - Number(a.newArrival);
      return Number(b.bestseller) - Number(a.bestseller) || b.rating - a.rating;
    });
  }, [
    category,
    collection,
    favouritesOnly,
    maxPrice,
    query,
    selectedOrigins,
    selectedSubcategories,
    sort,
    wishlist,
  ]);

  const clearFilters = () => {
    setSelectedOrigins([]);
    setSelectedSubcategories([]);
    setMaxPrice(180);
    setSort("featured");
    setSearchParams({});
  };

  const pageTitle = favouritesOnly
    ? "Your favourites"
    : collection === "deals"
    ? "Hot deals"
    : category === "All"
    ? "The Shop"
    : category;

  const sidebar = (
    <div className="filters">
      <div className="filters__mobile-head">
        <strong>Filter products</strong>
        <button
          type="button"
          className="icon-button"
          onClick={() => setFilterOpen(false)}
          aria-label="Close filters"
        >
          <FiX />
        </button>
      </div>
      <div className="filters__top">
        <span>Filters</span>
        <button type="button" onClick={clearFilters}>
          Clear all
        </button>
      </div>
      <FilterGroup title="Category">
        {productCategories.map((item) => (
          <label key={item} className="filter-radio">
            <input
              type="radio"
              name="category"
              checked={category === item}
              onChange={() => updateParam("category", item)}
            />
            <span>{item}</span>
            <small>
              {item === "All"
                ? productData.length
                : productData.filter((product) => product.category === item)
                    .length}
            </small>
          </label>
        ))}
      </FilterGroup>
      <FilterGroup title="Type">
        {productSubcategories.map((item) => {
          const count = productData.filter(
            (product) => product.subcategory === item
          ).length;
          if (!count) return null;
          return (
            <label key={item} className="filter-check">
              <input
                type="checkbox"
                checked={selectedSubcategories.includes(item)}
                onChange={() =>
                  toggleArrayValue(setSelectedSubcategories, item)
                }
              />
              <span>{item}</span>
              <small>{count}</small>
            </label>
          );
        })}
      </FilterGroup>
      <FilterGroup title="Origin">
        {origins.map((origin) => (
          <label key={origin} className="filter-check">
            <input
              type="checkbox"
              checked={selectedOrigins.includes(origin)}
              onChange={() => toggleArrayValue(setSelectedOrigins, origin)}
            />
            <span>{origin}</span>
          </label>
        ))}
      </FilterGroup>
      <FilterGroup title="Price">
        <div className="price-filter">
          <input
            type="range"
            min="40"
            max="180"
            step="2"
            value={maxPrice}
            onChange={(event) => setMaxPrice(Number(event.target.value))}
            aria-label="Maximum price"
          />
          <span>Up to HK${maxPrice}</span>
        </div>
      </FilterGroup>
      <button
        className="button button--primary filters__apply"
        type="button"
        onClick={() => setFilterOpen(false)}
      >
        Show {filteredProducts.length} products
      </button>
    </div>
  );

  return (
    <main className="shop-page">
      <PageMeta
        title={pageTitle}
        description="Browse artisan cheese, charcuterie and carefully selected grocery favourites."
      />
      <header className="shop-hero">
        <div className="container shop-hero__inner">
          <p className="eyebrow">Our market</p>
          <h1>{pageTitle}</h1>
          <p>
            {query
              ? `Results for “${query}”`
              : "Artisan food selected for flavour, provenance and the pleasure of sharing."}
          </p>
        </div>
      </header>

      <div className="container shop-layout">
        <aside className={`filter-sidebar ${filterOpen ? "is-open" : ""}`}>
          {sidebar}
        </aside>
        {filterOpen && (
          <button
            className="filter-backdrop"
            type="button"
            onClick={() => setFilterOpen(false)}
            aria-label="Close filters"
          />
        )}

        <section
          className="shop-results"
          aria-live="polite"
          aria-busy={loading}
        >
          <div className="shop-toolbar">
            <button
              className="filter-toggle"
              type="button"
              onClick={() => setFilterOpen(true)}
            >
              <FiFilter /> Filter
            </button>
            <p>
              <strong>{filteredProducts.length}</strong> products
            </p>
            <div className="shop-toolbar__search">
              <FiSearch />
              <input
                type="search"
                value={query}
                onChange={(event) => updateParam("q", event.target.value)}
                placeholder="Search products"
                aria-label="Search products"
              />
            </div>
            <label className="sort-select">
              <span>Sort</span>
              <select
                value={sort}
                onChange={(event) => setSort(event.target.value)}
              >
                <option value="featured">Featured</option>
                <option value="newest">New arrivals</option>
                <option value="price-low">Price: low to high</option>
                <option value="price-high">Price: high to low</option>
                <option value="name">Name</option>
              </select>
            </label>
          </div>

          {loading ? (
            <div
              className="product-grid product-grid--shop"
              aria-label="Loading products"
            >
              {Array.from({ length: 8 }).map((_, index) => (
                <div className="product-skeleton" key={index} />
              ))}
            </div>
          ) : filteredProducts.length ? (
            <div className="product-grid product-grid--shop">
              {filteredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          ) : (
            <div className="shop-empty">
              <span>Nothing on this shelf—yet.</span>
              <h2>Try a broader search</h2>
              <p>Remove a filter or explore the full market selection.</p>
              <button
                className="button button--primary"
                type="button"
                onClick={clearFilters}
              >
                Clear filters
              </button>
            </div>
          )}
        </section>
      </div>
    </main>
  );
}

export default Shop;
